'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

interface Course {
  id: string;
  title: string;
  instructor: string;
  instructorImage: string;
  instructorImageAlt: string;
  duration: string;
  level: string;
  rating: number;
  studentsEnrolled: number;
  price: number;
  thumbnail: string;
  thumbnailAlt: string;
  category: string;
  format: string;
  startDate: string;
  description: string;
}

interface ComparisonMatrixProps {
  courses: Course[];
  onRemoveCourse: (courseId: string) => void;
  onClose: () => void;
}

const ComparisonMatrix = ({ courses, onRemoveCourse, onClose }: ComparisonMatrixProps) => {
  if (courses.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-headline font-bold text-foreground">
            Course Comparison
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close comparison"
          >
            <Icon name="XMarkIcon" size={24} variant="outline" />
          </button>
        </div>

        <div className="flex-1 overflow-x-auto overflow-y-auto p-6">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left p-4 bg-muted font-headline font-bold text-foreground sticky left-0 z-10">
                  Feature
                </th>
                {courses.map((course) => (
                  <th key={course.id} className="p-4 bg-muted min-w-[280px]">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-full h-32 rounded-lg overflow-hidden">
                        <AppImage
                          src={course.thumbnail}
                          alt={course.thumbnailAlt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="font-headline font-bold text-foreground text-center line-clamp-2">
                        {course.title}
                      </h3>
                      <button
                        onClick={() => onRemoveCourse(course.id)}
                        className="text-xs text-error hover:text-error/80 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border">
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Instructor
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <AppImage
                          src={course.instructorImage}
                          alt={course.instructorImageAlt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="text-sm text-foreground">{course.instructor}</span>
                    </div>
                  </td>
                ))}
              </tr>

              <tr className="border-b border-border">
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Price
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center">
                    <span className="text-xl font-headline font-bold text-brand-primary">
                      ${course.price}
                    </span>
                  </td>
                ))}
              </tr>

              <tr className="border-b border-border">
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Rating
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Icon name="StarIcon" size={16} variant="solid" className="text-warning" />
                      <span className="font-semibold text-foreground">{course.rating}</span>
                    </div>
                  </td>
                ))}
              </tr>

              <tr className="border-b border-border">
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Students Enrolled
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center text-foreground">
                    {course.studentsEnrolled.toLocaleString()}
                  </td>
                ))}
              </tr>

              <tr className="border-b border-border">
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Duration
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center text-foreground">
                    {course.duration}
                  </td>
                ))}
              </tr>

              <tr className="border-b border-border">
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Skill Level
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center">
                    <span className="px-3 py-1 bg-muted text-foreground text-sm font-medium rounded-full">
                      {course.level}
                    </span>
                  </td>
                ))}
              </tr>

              <tr className="border-b border-border">
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Category
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center">
                    <span className="px-3 py-1 bg-brand-secondary text-brand-primary text-sm font-semibold rounded-full">
                      {course.category}
                    </span>
                  </td>
                ))}
              </tr>

              <tr className="border-b border-border">
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Format
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center text-foreground">
                    {course.format}
                  </td>
                ))}
              </tr>

              <tr className="border-b border-border">
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Start Date
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center text-foreground">
                    {course.startDate}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="p-4 font-medium text-foreground sticky left-0 bg-card z-10">
                  Action
                </td>
                {courses.map((course) => (
                  <td key={course.id} className="p-4 text-center">
                    <Link
                      href="/individual-course-page"
                      className="inline-block px-6 py-2 bg-brand-conversion text-white font-cta font-semibold rounded-md hover:bg-opacity-90 transition-all duration-300"
                    >
                      View Details
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComparisonMatrix;